import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { COMPANY, HERO_LINES, IMAGES } from "@/data/content";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.2, delay: 0.35 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacityFront = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Parallax texture */}
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroTexture}
          alt=""
          className="w-full h-full object-cover grayscale opacity-[0.18] contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-epic-bg via-epic-bg/70 to-epic-bg/30" />
      </motion.div>

      <motion.div
        style={{ opacity: opacityFront }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10 pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] text-epic-sub mb-6 md:mb-10 flex items-center gap-3"
        >
          <span className="inline-block w-8 h-px bg-epic-accent" />
          Confidential — {COMPANY.round} & Direct Equity · Accredited Investors
        </motion.p>

        <h1 className="font-grotesk font-black uppercase tracking-tighter text-epic-text leading-[0.86]">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className="block"
                style={{ fontSize: "clamp(2.75rem, 11vw, 9rem)" }}
              >
                {i === 1 ? (
                  <>
                    e<span className="text-epic-accent">P</span>IC Services
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 md:mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="max-w-md"
          >
            <p className="font-mono text-sm md:text-base text-epic-sub leading-relaxed">
              {COMPANY.tagline}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["Built for Advisors by Advisors", "Turnkey", "Ecosystem"].map((tag) => (
                <span
                  key={tag}
                  data-testid={`hero-tag-${tag}`}
                  className="border border-epic-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-epic-text"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="flex items-center gap-4"
          >
            <a
              href={COMPANY.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-meeting"
              className="group inline-flex items-center gap-2 bg-epic-accent px-7 py-4 text-white font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,transform] duration-300 hover:bg-epic-accentHover active:scale-95"
            >
              Book a Meeting
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#financials"
              data-testid="hero-view-model"
              className="inline-flex items-center gap-2 border border-epic-line px-7 py-4 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
            >
              View The Financial Model
            </a>
            <a
              href="https://epic-ecosystem.emergent.host/?utm_source=share"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-view-business-model"
              className="group inline-flex items-center gap-2 border border-epic-line px-7 py-4 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
            >
              View The Business Model
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10 pb-8"
      >
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-epic-sub">
          <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
          Scroll to explore
        </div>
      </motion.div>
    </section>
  );
};
